import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Bell, X, AlertTriangle, AlertCircle, Info, Check,
    FileText, Activity, Clock, Gauge, Lock, CreditCard, Link2, Link2Off,
    Calendar, Megaphone,
} from "lucide-react";
import { api } from "@/lib/api";

const SEVERITY = {
    critical: { color: "text-error-c",   bg: "bg-error/10",   border: "border-error/30",   Fallback: AlertCircle },
    warning:  { color: "text-warning-c", bg: "bg-warning/10", border: "border-warning/30", Fallback: AlertTriangle },
    info:     { color: "text-primary-c", bg: "bg-primary/10", border: "border-primary/30", Fallback: Info },
};

// Per-event icon. Falls back to severity icon if unmapped.
const EVENT_ICONS = {
    report_ready:              FileText,
    anomaly_detected:          Activity,
    weekly_reminder_tomorrow:  Clock,
    weekly_reminder_overdue:   AlertTriangle,
    limit_warning:             Gauge,
    limit_exhausted:           Lock,
    payment_success:           CreditCard,
    payment_failed:            CreditCard,
    mcc_linked:                Link2,
    mcc_lost:                  Link2Off,
    subscription_expiring:     Calendar,
    product_news:              Megaphone,
};

function iconFor(n) {
    return EVENT_ICONS[n.event_type || n.kind] || SEVERITY[n.severity]?.Fallback || Info;
}

function formatRelative(iso) {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "щойно";
    if (diff < 3600) return `${Math.floor(diff / 60)} хв тому`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} год тому`;
    if (diff < 172800) return "вчора";
    if (diff < 604800) return `${Math.floor(diff / 86400)} дн тому`;
    return new Date(iso).toLocaleDateString("uk-UA", { day: "2-digit", month: "short" });
}

export default function NotificationBell() {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(false);
    const ref = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/notifications?limit=10");
            setItems(r.data?.items || []);
            setUnread(r.data?.unread || 0);
        } catch {}
        setLoading(false);
    };

    useEffect(() => { load(); }, []);
    useEffect(() => {
        const t = setInterval(load, 60_000);
        return () => clearInterval(t);
    }, []);
    useEffect(() => {
        const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", fn);
        return () => document.removeEventListener("mousedown", fn);
    }, []);

    const markRead = async (id, e) => {
        e?.stopPropagation();
        try {
            await api.post(`/notifications/${id}/read`);
            setItems(prev => prev.map(it => it.notification_id === id ? { ...it, read: true } : it));
            setUnread(u => Math.max(0, u - 1));
        } catch {}
    };

    const markAllRead = async () => {
        try {
            await api.post("/notifications/read-all");
            setItems(prev => prev.map(it => ({ ...it, read: true })));
            setUnread(0);
        } catch {}
    };

    const openNotif = async (n) => {
        if (!n.read) markRead(n.notification_id);
        if (n.action_url) {
            setOpen(false);
            navigate(n.action_url);
        }
    };

    return (
        <div className="relative" ref={ref} data-testid="notif-bell-root">
            <button
                onClick={() => setOpen(v => !v)}
                className="relative p-2 rounded-md text-secondary-c hover:text-text-primary hover:bg-surface-hover transition"
                data-testid="notif-bell-btn"
                title="Сповіщення"
            >
                <Bell size={18} />
                {unread > 0 && (
                    <span
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-error text-white text-[10px] font-mono font-bold flex items-center justify-center"
                        data-testid="notif-bell-count"
                    >
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="absolute right-0 top-12 dropdown-panel rounded-xl w-[360px] sm:w-[420px] max-h-[560px] overflow-hidden z-50 flex flex-col"
                    data-testid="notif-bell-panel"
                >
                    <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                        <div className="font-display font-semibold text-text-primary">Сповіщення</div>
                        <div className="flex items-center gap-2">
                            {unread > 0 && (
                                <button
                                    onClick={markAllRead}
                                    className="text-xs font-mono text-secondary-c hover:text-text-primary transition"
                                    data-testid="notif-mark-all-read"
                                >
                                    Прочитати все
                                </button>
                            )}
                            <button onClick={() => setOpen(false)} className="text-secondary-c hover:text-text-primary">
                                <X size={14} />
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {loading && <div className="p-6 text-center text-sm text-secondary-c font-mono">Завантаження…</div>}
                        {!loading && items.length === 0 && (
                            <div className="p-8 text-center" data-testid="notif-empty">
                                <Bell size={28} className="mx-auto text-faint-c opacity-50" />
                                <p className="mt-3 text-sm text-secondary-c">Поки що немає сповіщень.</p>
                                <p className="mt-1 text-xs text-faint-c">Ми перевіряємо ваші кампанії щодня о 08:00 UTC.</p>
                            </div>
                        )}
                        {!loading && items.map(n => {
                            const sev = SEVERITY[n.severity] || SEVERITY.info;
                            const Icon = iconFor(n);
                            return (
                                <button
                                    key={n.notification_id}
                                    type="button"
                                    onClick={() => openNotif(n)}
                                    className={`w-full text-left px-4 py-3 border-b border-border transition hover:bg-surface-hover/30 ${n.read ? "" : "bg-primary/5"}`}
                                    data-testid={`notif-item-${n.notification_id}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center border ${sev.bg} ${sev.border}`}>
                                            <Icon size={14} className={sev.color} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <div className={`font-medium text-sm ${sev.color} truncate`}>{n.title}</div>
                                                {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-primary-c shrink-0" />}
                                            </div>
                                            {n.campaign_name && (
                                                <div className="mt-0.5 text-[11px] font-mono text-faint-c truncate">{n.campaign_name}</div>
                                            )}
                                            <p className="mt-1.5 text-xs text-text-primary/85 leading-snug line-clamp-2">{n.message}</p>
                                            <div className="mt-2 flex items-center justify-between gap-2">
                                                <span className="text-[10px] font-mono text-faint-c">{formatRelative(n.created_at)}</span>
                                                <div className="flex items-center gap-3">
                                                    {n.action_label && n.action_url && (
                                                        <span className="text-[10px] font-mono text-primary-c">{n.action_label} →</span>
                                                    )}
                                                    {!n.read && (
                                                        <button
                                                            onClick={(e) => markRead(n.notification_id, e)}
                                                            className="text-[10px] font-mono text-secondary-c hover:text-text-primary inline-flex items-center gap-1"
                                                            data-testid={`notif-read-${n.notification_id}`}
                                                        >
                                                            <Check size={11} /> Прочитати
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="border-t border-border px-4 py-2.5 text-center bg-surface/30">
                        <button
                            onClick={() => { setOpen(false); navigate("/notifications"); }}
                            className="text-xs font-medium text-primary-c hover:underline"
                            data-testid="notif-view-all"
                        >
                            Дивитись всі →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}